import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { FlexContainer } from '@/ui/styled-ui';
import {
  colorGrayDark,
  colorGrayOpacity,
  colorPurpleText,
  colorRedLight,
  colorRedMain,
  defaultPadding,
  mediumPadding,
} from '@/utils/helpers/css-variables.helper';
import { t } from 'i18next';
import { Chart } from 'primereact/chart';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { tooltipDefaultSettings } from '../charts.helper';

const StyledChart = styled.div<{ isMainDashboard?: boolean }>`
  position: relative;
  .min,
  .max {
    position: absolute;
    font-size: var(--xsmall-font-size);
    bottom: var(--xxsmall-padding);
  }

  .min {
    left: ${({ isMainDashboard }) =>
      isMainDashboard ? '-16px' : 'calc(2 * var(--default-padding))'};
    bottom: ${({ isMainDashboard }) =>
      isMainDashboard ? 'var(--default-padding)' : '0px'};
  }
  .max {
    right: ${({ isMainDashboard }) =>
      isMainDashboard ? '-26px' : 'calc(2.75 * var(--medium-padding))'};
    bottom: ${({ isMainDashboard }) =>
      isMainDashboard ? 'var(--default-padding)' : '0px'};
  }

  @media screen and (max-width: 1800px) {
    .min {
      left: ${({ isMainDashboard }) =>
        isMainDashboard ? '-8px' : 'calc(1.3 * var(--default-padding))'};
      bottom: ${({ isMainDashboard }) =>
        isMainDashboard ? 'var(--default-padding)' : '0px'};
    }
    .max {
      right: ${({ isMainDashboard }) =>
        isMainDashboard ? '-18px' : 'calc(1.7 * var(--medium-padding))'};
      bottom: ${({ isMainDashboard }) =>
        isMainDashboard ? 'var(--default-padding)' : '0px'};
    }
  }
`;

const getChartWidth = (
  isMainDashboard?: boolean,
  isSmallChart?: boolean,
): string => {
  if (isMainDashboard) {
    return isSmallChart ? '200px' : '340px';
  }
  return isSmallChart ? '280px' : '400px';
};

const getChartHeight = (
  isMainDashboard?: boolean,
  isSmallChart?: boolean,
): string => {
  if (isMainDashboard) {
    return isSmallChart ? '120px' : '200px';
  }
  return isSmallChart ? '150px' : '200px';
};

export type SemiDoughnutChartProps = {
  id: string;
  value: number;
  controlValue: number;
  valueTooltipSuffix?: string;
  controlValueTooltipSuffix?: string;
  min?: number;
  max?: number;
  isMainDashboard?: boolean;
  isPurple?: boolean;
};

export const SemiDoughnutChart: React.FC<SemiDoughnutChartProps> = ({
  id,
  value,
  controlValue,
  valueTooltipSuffix,
  controlValueTooltipSuffix,
  min = 0,
  max = 100,
  isMainDashboard,
  isPurple = false,
}) => {
  const navigate = useNavigate();
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});

  const isSmallChartCheck = window.matchMedia('(max-width: 1800px)');
  const [isSmallChart, setIsSmallChart] = useState(isSmallChartCheck.matches);
  const docStyle = getComputedStyle(document.documentElement);

  const handleWindowSizeChange = () => {
    setIsSmallChart(isSmallChartCheck.matches);
  };

  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    };
  }, []);

  useEffect(() => {
    const remainingFromValue = max - value;
    const values = [value, remainingFromValue];
    const remainingFromControlValue = max - controlValue;
    const controlValues = [controlValue, remainingFromControlValue];

    const data = {
      datasets: [
        {
          label: null,
          data: values,
          backgroundColor: [
            isPurple ? colorPurpleText(docStyle) : colorRedMain(docStyle),
            colorGrayDark(docStyle),
          ],
          borderColor: 'transparent',
        },
        {
          label: null,
          data: controlValues,
          backgroundColor: [
            isPurple
              ? `${colorPurpleText(docStyle)}66`
              : colorRedLight(docStyle),
            colorGrayOpacity(docStyle),
          ],
          borderColor: 'transparent',
        },
      ],
      labels: null,
    };
    const options = {
      cutout: '70%',
      plugins: {
        legend: {
          display: false,
        },
        datalabels: false,
        tooltip: {
          ...tooltipDefaultSettings(),
          callbacks: {
            title: () => null,
            label: (tooltipItem: any) =>
              `${t('generic.score')} ${
                tooltipItem.datasetIndex === 0 ? value : controlValue
              }`,
            afterLabel: (tooltipItem: any) =>
              tooltipItem.datasetIndex === 0
                ? valueTooltipSuffix
                : controlValueTooltipSuffix,
          },
        },
      },
      layout: {
        padding: {
          left: isMainDashboard
            ? '0'
            : mediumPadding(docStyle)?.replace('px', ''),
          right: isMainDashboard
            ? '0'
            : mediumPadding(docStyle)?.replace('px', ''),
          bottom: defaultPadding(docStyle)?.replace('px', ''),
        },
      },
      rotation: -90,
      circumference: 180,
      responsive: false,
      maintainAspectRatio: false,
      onHover: (event: any, activeElements: any[]) => {
        if (!isMainDashboard) return;
        if (!event?.native?.target?.style) return;
        event.native.target.style.cursor =
          activeElements.length > 0 ? 'pointer' : 'default';
      },
      onClick: (event: any, activeElements: any[]) => {
        if (activeElements.length > 0 && isMainDashboard) {
          navigate(RedirectPaths[RedirectPathsEnum.SECURITY_CULTURE_SURVEY]());
        }
      },
    };

    setChartData(data);
    setChartOptions(options);
  }, [value, controlValue]);

  return (
    <FlexContainer direction="column" width="min-content">
      <StyledChart
        key={isSmallChart.toString()}
        isMainDashboard={isMainDashboard}
      >
        <Chart
          id={id}
          type="doughnut"
          data={chartData}
          options={chartOptions}
          className="chart"
          width={getChartWidth(isMainDashboard, isSmallChart)}
          height={getChartHeight(isMainDashboard, isSmallChart)}
        />

        <span className="min">{min}</span>
        <span className="max">{max}</span>
      </StyledChart>
    </FlexContainer>
  );
};
